import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { memberApi, formatMemberError } from "@/lib/memberClient";

const MemberContext = createContext(null);

export function MemberProvider({ children }) {
  const [member, setMember] = useState(null); // null = loading, false = guest, object = signed in
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("pp_member_token");
    if (!token) {
      setMember(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await memberApi.get("/member/me");
      setMember(data);
    } catch {
      localStorage.removeItem("pp_member_token");
      setMember(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const _claimGuest = async () => {
    try {
      const gt = localStorage.getItem("pp_guest_token");
      if (gt && gt.length >= 8) {
        await memberApi.post("/member/pay-scratch/claim-guest", { guest_token: gt });
      }
    } catch { /* silent */ }
  };

  const login = async (email, password) => {
    try {
      const { data } = await memberApi.post("/member/login", { email, password });
      localStorage.setItem("pp_member_token", data.token);
      setMember(data.member);
      await _claimGuest();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatMemberError(e) };
    }
  };

  const signup = async (payload) => {
    try {
      const { data } = await memberApi.post("/member/signup", payload);
      localStorage.setItem("pp_member_token", data.token);
      setMember(data.member);
      await _claimGuest();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatMemberError(e) };
    }
  };

  const logout = () => {
    localStorage.removeItem("pp_member_token");
    setMember(false);
  };

  return (
    <MemberContext.Provider value={{ member, loading, login, signup, logout, refresh, setMember }}>
      {children}
    </MemberContext.Provider>
  );
}

export function useMember() {
  return useContext(MemberContext);
}
